// CreateAdvanceRequest.js
import React, { useContext, useEffect, useState } from 'react';
import {
    Button,
    Grid,
    TextField,
    Divider,
    Typography,
    Box,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Paper,
    IconButton,
    Stack,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Collapse
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WorkIcon from '@mui/icons-material/Work';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import DateRangeIcon from '@mui/icons-material/DateRange';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import StyledAutocomplete from '../../components/UIComponents/StyledAutocomplete';
import StyledTextField from '../../components/UIComponents/StyledTextField';
import { useNavigate } from "react-router-dom";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import theme from '../../assets/theme';
import { useLoading } from '../../context/loading.context';
import { Employeedetails, getReportData, saveDoc, searchLink } from '../../service/network';
import moment from 'moment/moment';
import { AuthContext } from '../../context/auth.context';
import { useNotification } from '../../context/notification.context';


function CreateAdvanceRequest() {
    const navigate = useNavigate();
    const [showRepayment, setShowRepayment] = useState(false);
    const [repaymentPlan, setRepaymentPlan] = useState('none');
    const { showLoader, hideLoader } = useLoading();
    const [employees, setEmployees] = useState([]);
    const [approvers, setApprovers] = useState([]);
    const [branchOptions, setBranchOptions] = useState([]);

    const [stats, setStats] = useState({});
    const [modes, setModes] = useState([]);
    const [filters, setFilters] = useState({
        company: '',
        branch: '',
        approverBranch: ''
    });
    const [advanceData, setAdvanceData] = useState({
        employee: null,
        requestDate: new Date().toISOString().split('T')[0],
        advanceType: '',
        amount: '',
        approvedBy: null,
        referredBy: null,
        eligibility: 'eligible',
        description: '',
        repaymentFrequency: 'none',
        installmentCount: 0,
        startDate: '',
        paymentmode: null
    });

    const { selLocation } = useContext(AuthContext);
    const { showNotification } = useNotification();
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const fetchOptions = async (doctype, setterFunction) => {
        try {
            const queryParams = new URLSearchParams({
                doctype,
                fields: JSON.stringify(['name'])
            });

            const response = await Employeedetails(queryParams.toString());

            if (!response?.data?.message?.values) {
                throw new Error(`No data found for ${doctype}`);
            }

            const options = response.data.message.values.map(v => v[0]);
            setterFunction(options);
        } catch (error) {
            console.error(`Failed to fetch ${doctype} options:`, error);
            setterFunction([]);
        }
    };

    useEffect(() => {
        fetchOptions('Branch', setBranchOptions);
    }, []);

    useEffect(() => {
        if (filters.branch) {
            fetchEmployees(filters.branch);
            // Clear selected employee when branch changes
            handleChange('employee', null);
        } else {
            // If no branch selected, fetch all employees
            fetchEmployees();
        }
    }, [filters.branch]);

    useEffect(() => {
        if (filters.approverBranch) {
            fetchApprovers(filters.approverBranch);
            // Clear selected approver when branch changes
            handleChange('approvedBy', null);
        } else {
            // If no branch selected, fetch all approvers
            fetchApprovers();
        }
    }, [filters.approverBranch]);

    const handleChange = (field, value) => {
        setAdvanceData(prev => ({
            ...prev,
            [field]: value
        }));

        // Automatically show repayment section when amount is entered
        if (field === 'amount' && value > 0) {
            setShowRepayment(true);
        }
    };

    const handleRepaymentPlanChange = (event) => {
        const value = event.target.value;
        setRepaymentPlan(value);
        handleChange('repaymentFrequency', value);

        // Set default installment count based on selection
        if (value === 'weekly') {
            handleChange('installmentCount', 4);
        } else if (value === 'monthly') {
            handleChange('installmentCount', 3);
        } else {
            handleChange('installmentCount', 0);
        }
    };

    const handleSubmit = () => {
        console.log('Advance Request Submitted:', advanceData);
        // Here you would typically send data to your backend
        createEmployeeAdvance();
    };

    const handleClose = () => {
        navigate('/advance-request/list');
    };

    function mapKeyValueResponse(apiResponse) {
        if (!apiResponse.values || !apiResponse.keys || !Array.isArray(apiResponse.values)) {
            return [];
        }

        return apiResponse.values.map(valueArray => {
            const obj = {};
            apiResponse.keys.forEach((key, index) => {
                obj[key] = valueArray[index];
            });
            return obj;
        });
    }


    const createEmployeeAdvance = async () => {
        try {
            showLoader();
            let payload = {
                "employee": advanceData.employee.name,
                "custom_approval_status": "Pending",
                "custom_approver": advanceData.approvedBy.name,
                "posting_date": moment(advanceData.requestDate).format("YYYY-MM-DD"),
                "company": selLocation[0],
                "purpose": advanceData.description,
                "advance_amount": parseFloat(advanceData.amount),
                "mode_of_payment": advanceData.paymentmode.value,
                "repay_unclaimed_amount_from_salary": 1,
                "exchange_rate": 1,
                "doctype": "Employee Advance"
            }
            let resp = await saveDoc({
                doc: JSON.stringify(payload),
                action: "Submit",
            });
            showNotification(
                'success',
                'Success',
                "Advance created Successfully"
            );
            navigate('/advance-request/list');
        } catch (err) {
            console.log(err);
            showNotification('error', 'Error', 'Failed to create employee advance');
        } finally {
            hideLoader();
        }
    }

    const fetchEmployees = async (branch = null) => {
        try {
            showLoader(true);
            const filters = [["company", "=", selLocation[0]]];
            if (branch) {
                filters.push(["branch", "=", branch]);
            }

            const queryParams = {
                doctype: 'Employee',
                fields: [
                    "name",
                    "employee_name",
                    "status",
                    "designation",
                    "department",
                    "gender",
                    "ctc",
                    "salary_currency",
                    "date_of_joining",
                    "company",
                    "branch"
                ],
                filters: filters,
                order_by: 'modified desc'
            };

            const response = await getReportData(queryParams);
            let emps = mapKeyValueResponse(response.data.message);
            setEmployees(emps);

        } catch (error) {
            console.error('Fetch error:', error);
            setEmployees([]);
        } finally {
            hideLoader();
        }
    };

    const fetchApprovers = async (branch = null) => {
        try {
            showLoader(true);
            const filters = [["company", "=", selLocation[0]]];
            if (branch) {
                filters.push(["branch", "=", branch]);
            }

            // Assuming approvers are also employees with a specific role
            const queryParams = {
                doctype: 'Employee',
                fields: [
                    "name",
                    "employee_name",
                    "status",
                    "designation",
                    "department",
                    "branch"
                ],
                filters: filters,
                order_by: 'modified desc'
            };

            const response = await getReportData(queryParams);
            let approversList = mapKeyValueResponse(response.data.message);
            setApprovers(approversList);

        } catch (error) {
            console.error('Fetch error:', error);
            setApprovers([]);
        } finally {
            hideLoader();
        }
    };

    const fetchPaymentModes = async () => {
        try {
            let params = {
                txt: "",
                doctype: "Mode of Payment",
                ignore_user_permissions: 0,
                reference_doctype: "Employee Advance",
                page_length: 100
            };
            let modes_pmt = await searchLink(params);
            setModes(modes_pmt.data.message);
        } catch (err) {
            console.log(err);
            setModes([]);
        }
    }

    const fetchEmployeeStats = async () => {
        if (advanceData.employee) {
            try {
                showLoader();
                let attendance_records = await getReportData({
                    doctype: 'Attendance',
                    fields: [
                        "name",
                        "employee",
                        "status",
                        "attendance_date"
                    ],
                    filters: [["employee", "=", advanceData.employee.name], ["attendance_date", ">=", moment().startOf('month').format("YYYY-MM-DD")], ["attendance_date", "<=", moment().format("YYYY-MM-DD")], ["docstatus", "=", 1]]
                });
                let mappped_att = mapKeyValueResponse(attendance_records.data.message);
                let present_count = 0;
                for (let att of mappped_att) {
                    if (att.status === "Present") {
                        present_count += 1;
                    } else if (att.status === "Half Day") {
                        present_count += 0.5;
                    }
                }
                let advance_records = await getReportData({
                    doctype: 'Employee Advance',
                    fields: [
                        "name",
                        "employee",
                        "status",
                        "advance_amount",
                        "posting_date"
                    ],
                    filters: [["employee", "=", advanceData.employee.name], ["docstatus", "=", 1]],
                    order_by: 'posting_date desc'
                });
                let mappped_adv = mapKeyValueResponse(advance_records.data.message);
                let ratio = (present_count / moment().daysInMonth()) * advanceData.employee.ctc;

                let advance_pendings_records = await getReportData({
                    doctype: 'Employee Advance',
                    fields: [
                        "name",
                        "employee",
                        "status",
                        "advance_amount",
                        "posting_date",
                        "paid_amount",
                        "return_amount"
                    ],
                    filters: [["employee", "=", advanceData.employee.name], ["docstatus", "=", 1], ["status", "!=", "Returned"]],
                    order_by: 'posting_date desc'
                });
                let unpaid_mappings = mapKeyValueResponse(advance_pendings_records.data.message);
                let pnd_amt = 0;
                for (let ump of unpaid_mappings) {
                    pnd_amt += (ump.paid_amount - ump.return_amount);
                }
                setStats({
                    last_advance_amount: (mappped_adv.length > 0) ? mappped_adv[0].advance_amount : 0,
                    posting_date: (mappped_adv.length > 0) ? mappped_adv[0].posting_date : 0,
                    present: present_count,
                    eligible_amount: ratio,
                    pending_count: unpaid_mappings.length,
                    pending_amount: pnd_amt
                });
            } catch (err) {
                console.log(err);
                setStats({});
            } finally {
                hideLoader();
            }
        }
    }

    useEffect(() => {
        fetchEmployees();
        fetchApprovers();
        fetchPaymentModes();
    }, [selLocation]);

    useEffect(() => {
        fetchEmployeeStats();
    }, [advanceData.employee]);

    return (
        <Box
            sx={{
                width: '100%',
                p: { xs: 1, sm: 2 },
                boxSizing: 'border-box'
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    width: '100%',
                    minHeight: 800,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 3
                }}
            >
                {/* Header Section */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <IconButton onClick={handleClose}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h5" color="primary.main">
                            Create Advance Request
                        </Typography>
                    </Stack>

                    <Box>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{ borderRadius: '8px', textTransform: 'none', px: 3, mr: 2 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            sx={{ borderRadius: '8px', textTransform: 'none', px: 3 }}
                            disabled={!advanceData.employee || !advanceData.amount || !advanceData.approvedBy || !advanceData.paymentmode}
                        >
                            Submit Request
                        </Button>
                    </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Main Form */}
                <Grid container spacing={3}>
                    {/* Employee Information */}
                    <Grid size={2}>
                        <StyledAutocomplete
                            label="Employee Branch"
                            options={branchOptions}
                            value={filters.branch}
                            onChange={(e, newValue) => {
                                handleFilterChange('branch', newValue);
                            }}
                            fullWidth
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <WorkIcon color="action" />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                    <Grid size={3}>
                        <StyledAutocomplete
                            label="Employee *"
                            options={employees}
                            getOptionLabel={(option) => `${option.employee_name} (${option.name})`}
                            value={advanceData.employee}
                            onChange={(e, newValue) => handleChange('employee', newValue)}
                            required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonIcon color="action" />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                    <Grid size={2}>
                        <StyledTextField
                            label="Request Date *"
                            type="date"
                            value={advanceData.requestDate}
                            onChange={(e) => handleChange('requestDate', e.target.value)}
                            required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <DateRangeIcon color="action" />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                    {/* Advance Details */}
                    <Grid size={2}>
                        <StyledAutocomplete
                            label="Mode of Payment"
                            options={modes}
                            value={advanceData.paymentmode}
                            onChange={(e, newValue) => handleChange('paymentmode', newValue)}
                            required
                            getOptionLabel={(option) => `${option.value}`}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <BadgeIcon color="action" />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                    <Grid size={2}>
                        <StyledTextField
                            label="Request Amount *"
                            type="number"
                            value={advanceData.amount}
                            onChange={(e) => handleChange('amount', e.target.value)}
                            required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Typography color="textSecondary" fontWeight="bold">₹</Typography>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                    {/* Approver Information */}
                    <Grid size={2}>
                        <StyledAutocomplete
                            label="Approver Branch"
                            options={branchOptions}
                            value={filters.approverBranch}
                            onChange={(e, newValue) => {
                                handleFilterChange('approverBranch', newValue);
                            }}
                            fullWidth
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <WorkIcon color="action" />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                    <Grid size={2}>
                        <StyledAutocomplete
                            label="Approver *"
                            options={approvers}
                            getOptionLabel={(option) => `${option.employee_name} (${option.name})`}
                            value={advanceData.approvedBy}
                            onChange={(e, newValue) => handleChange('approvedBy', newValue)}
                            required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <ThumbUpIcon color="action" />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        {/* <StyledAutocomplete
                            label="Referred By"
                            options={referrers}
                            getOptionLabel={(option) => `${option.name} (${option.id})`}
                            value={advanceData.referredBy}
                            onChange={(e, newValue) => handleChange('referredBy', newValue)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonAddAlt1Icon color="action" />
                                    </InputAdornment>
                                )
                            }}
                        /> */}
                    </Grid>

                    {/* Description */}
                    <Grid size={4}>
                        <StyledTextField
                            label="Description *"
                            value={advanceData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            required
                        />
                    </Grid>

                    {/* Employee Advance & Salary Summary Section */}
                    {advanceData.employee &&
                    <Grid size={12}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: 3,
                                    backgroundColor: '#f8fbff',
                                    border: '1px solid #e0e8f5',
                                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                                }}
                            >
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                        Employee Advance & Salary Overview
                                    </Typography>
                                    {/* <Chip
                                        label="Eligible"
                                        color="success"
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontWeight: 600 }}
                                    /> */}
                                </Box>

                                <Grid container spacing={2}>
                                    {/* First Row */}
                    <Grid size={2}>
                                        <Paper elevation={0} sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            backgroundColor: 'white',
                                            border: '1px solid #e0e8f5',
                                            height: '100%'
                                        }}>
                                            <Box display="flex" alignItems="center" gap={2}>
                                                <Box sx={{
                                                    p: 1.5,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#e3f2fd',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <AttachMoneyIcon color="primary" fontSize="small" />
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Annual CTC</Typography>
                                                    <Typography variant="subtitle1" fontWeight={600}>₹{advanceData.employee.ctc}</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        per month
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>

                    <Grid size={2}>
                                        <Paper elevation={0} sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            backgroundColor: 'white',
                                            border: '1px solid #e0e8f5',
                                            height: '100%'
                                        }}>
                                            <Box display="flex" alignItems="center" gap={2}>
                                                <Box sx={{
                                                    p: 1.5,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#e8f5e9',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <CalendarTodayIcon color="success" fontSize="small" />
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Attendance</Typography>
                                                    <Typography variant="subtitle1" fontWeight={600}>{stats.present ?? 0}/{moment().daysInMonth()} days</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        present
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>

                    <Grid size={2}>
                                        <Paper elevation={0} sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            backgroundColor: 'white',
                                            border: '1px solid #e0e8f5',
                                            height: '100%'
                                        }}>
                                            <Box display="flex" alignItems="center" gap={2}>
                                                <Box sx={{
                                                    p: 1.5,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#fff8e1',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <CurrencyRupeeIcon color="warning" fontSize="small" />
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Eligible Advance</Typography>
                                                    <Typography variant="subtitle1" fontWeight={600}>₹{stats.eligible_amount ? (stats.eligible_amount / 2).toFixed(0) : 0}</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        50% of accruel
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>

                                    <Grid size={2}>
                                        <Paper elevation={0} sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            backgroundColor: 'white',
                                            border: '1px solid #e0e8f5',
                                            height: '100%'
                                        }}>
                                            <Box display="flex" alignItems="center" gap={2}>
                                                <Box sx={{
                                                    p: 1.5,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#ffebee',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <DescriptionIcon color="error" fontSize="small" />
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Pending Advances</Typography>
                                                    <Typography variant="subtitle1" fontWeight={600} color="error">₹{stats.pending_amount ?? 0}</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        {stats.pending_count ?? 0} active advance
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>

                                    {/* Second Row - Additional Details */}
                    <Grid size={2}>
                                        <Paper elevation={0} sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            backgroundColor: 'white',
                                            border: '1px solid #e0e8f5',
                                            height: '100%'
                                        }}>
                                            <Box display="flex" alignItems="center" gap={2}>
                                                <Box sx={{
                                                    p: 1.5,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#f3e5f5',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <DateRangeIcon color="secondary.main" fontSize="small" />
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Joining Date</Typography>
                                                    <Typography variant="subtitle1" fontWeight={600}>{advanceData.employee.date_of_joining ?? "NA"}</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        {Math.abs(moment(advanceData.employee.date_of_joining).diff(moment(), 'years', true)).toFixed(1)} years tenure
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>

                    <Grid size={2}>
                                        <Paper elevation={0} sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            backgroundColor: 'white',
                                            border: '1px solid #e0e8f5',
                                            height: '100%'
                                        }}>
                                            <Box display="flex" alignItems="center" gap={2}>
                                                <Box sx={{
                                                    p: 1.5,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#e0f7fa',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <EventAvailableIcon color="info" fontSize="small" />
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Last Advance</Typography>
                                                    <Typography variant="subtitle1" fontWeight={600}>{stats.posting_date ?? "NA"}</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        ₹{stats.last_advance_amount ?? "NA"}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>}
                </Grid>
            </Paper>
        </Box>
    );
}

export default CreateAdvanceRequest;